import React, { Fragment, useState } from 'react';
import styled from 'styled-components';
import { observer } from "mobx-react-lite";
import { TileAssetModel, TileImageUsage, allTileImageUsages, availableImageUsagesForLayerType, TileVisibility, tileVisibilities } from '../../../shared/resources/TileAssetModel';
import { tileOffsetAndSizeStep, conflictResolutionOriginStep } from '../../../shared/data/mapElementSorting';
import { faCog, faDownload, faExclamationCircle, faMinusCircle, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { editorClient } from '../../communication/EditorClient';
import { useTranslation } from 'react-i18next';
import { gameConstants } from '../../data/gameConstants';
import { PlaneTransitModel } from '../../../shared/resources/PlaneTransitModel';
import { IntInputField } from '../shared/IntInputField';
import { FloatInputField } from '../shared/FloatInputField';
import { undoableSelectTileAsset } from '../../stores/undo/operation/SetPlacementSelectionOp';
import { Direction } from "../../../shared/resources/DirectionHelper";
import { clamp } from 'curve-interpolator';
import { TileLayerType } from '../../../shared/resources/TileLayerType';
import { sharedStore } from '../../stores/SharedStore';
import { tileAssetEditorStore } from '../../stores/TileAssetEditorStore';
import { tileImageStore } from '../../stores/TileImageStore';
import { errorStore } from '../../stores/ErrorStore';
import { gameStore } from '../../stores/GameStore';
const Container = styled.table.withConfig({
  displayName: "TileAssetConfigurator__Container",
  componentId: "sc-1yoawbx-0"
})(["background:white;border:1px solid darkgray;padding:3px;margin:3px;"]);
const Button = styled.button.withConfig({
  displayName: "TileAssetConfigurator__Button",
  componentId: "sc-1yoawbx-1"
})(["min-width:32px;min-height:24px;"]);
const DeleteButton = styled(Button).withConfig({
  displayName: "TileAssetConfigurator__DeleteButton",
  componentId: "sc-1yoawbx-2"
})(["color:#DD0000;"]);
const ErrorDiv = styled.div.withConfig({
  displayName: "TileAssetConfigurator__ErrorDiv",
  componentId: "sc-1yoawbx-3"
})(["color:#DD0000;"]);
const FileUploadButton = styled.input.withConfig({
  displayName: "TileAssetConfigurator__FileUploadButton",
  componentId: "sc-1yoawbx-4"
})(["overflow:hidden;width:93px;"]);
const CoordinateInput = observer(({
  disabled,
  positionProps,
  yCoordinate,
  changeAssetDetail
}) => {
  const setCoordinate = value => {
    if (yCoordinate) {
      positionProps.setY(value);
    } else {
      positionProps.setX(value);
    }

    if (changeAssetDetail) changeAssetDetail();
  };

  const value = positionProps ? yCoordinate ? positionProps.y : positionProps.x : 0;
  return /*#__PURE__*/React.createElement(IntInputField, {
    disabled: disabled,
    value: value,
    onChange: setCoordinate
  });
});
const VisibilityInputWrapper = styled.span.withConfig({
  displayName: "TileAssetConfigurator__VisibilityInputWrapper",
  componentId: "sc-1yoawbx-5"
})(["input[type=\"radio\"]{opacity:0;position:fixed;width:0;}&.ShowAlways{label{border-color:#4c4;color:#4c4;}input[type=\"radio\"]:checked + label{color:white;background-color:#4c4;}}&.ComplexOnly{label{border-color:#ff8800;color:#ff8800;}input[type=\"radio\"]:checked + label{color:white;background-color:#ff8800;}}&.ProductionOnly{label{border-color:orangered;color:orangered;}input[type=\"radio\"]:checked + label{color:white;background-color:orangered;}}label{display:inline-block;background-color:white;margin:1px;padding:2px;border:1px solid #444;}input[type=\"radio\"]:focus + label{border-color:black;}"]);

async function download(id, tileImageUsage) {
  const url = tileImageStore.downloadUrl(id, tileImageUsage);
  const a = document.createElement("a"); //document.body.appendChild(a);
  //a.style.display = "none";

  a.href = url;
  a.download = id + "_" + TileImageUsage[tileImageUsage] + ".png";
  a.click();
}

const TileConfiguration = observer(({
  id
}) => {
  var _currentTile$planeTra, _currentTile$planeTra2, _currentTile$planeTra3;

  const {
    t
  } = useTranslation();
  const currentTile = sharedStore.getTileAsset(id);
  const displayId = id;
  const anyAssetStillLoading = allTileImageUsages.some(usage => tileImageStore.isLoading(id, usage));

  const setLocalizedName = value => {
    currentTile.localizedName.set(gameStore.languageKey, value);
    changeAssetDetail();
  };

  const setTags = value => {
    currentTile.setTags(value);
    changeAssetDetail();
  };

  const setVisibilityInEditor = value => {
    console.log(value);
    currentTile.setVisibilityInEditor(value);
    changeAssetDetail();
  };

  const setFrames = (imageProperties, value) => {
    imageProperties.setFrames(value);
    changeAssetDetail();
  };

  const setAnimationDuration = (imageProperties, value) => {
    imageProperties.setAnimationDuration(value);
    changeAssetDetail();
  };

  const setLayer = layerIdx => {
    currentTile.setLayerType(layerIdx == 0 ? TileLayerType.Ground : TileLayerType.Decoration);

    if (currentTile.layerType === TileLayerType.Ground) {
      currentTile.size.applyCeil();
      currentTile.resetOffset();
    } else {
      currentTile.setImageProperties(null, TileImageUsage.WaterMaskForeground);
      currentTile.setImageProperties(null, TileImageUsage.Foreground);
      currentTile.setImageProperties(null, TileImageUsage.WaterMaskForeground);
    }

    changeAssetDetail();
  };

  const setTransitHeightDifference = value => {
    currentTile.planeTransit.setHeightDifference(value);
    changeAssetDetail();
  };

  const setTargetXOffset = value => {
    currentTile.planeTransit.setTargetXOffset(value);
    changeAssetDetail();
  };

  const setTargetYOffset = value => {
    currentTile.planeTransit.setTargetYOffset(value);
    changeAssetDetail();
  };

  const setPlaneTransit = direction => {
    if (direction >= 0) {
      if (currentTile.planeTransit) {
        currentTile.planeTransit.setDirection(direction);
      } else {
        currentTile.setPlaneTransit(new PlaneTransitModel({
          direction
        }));
      }
    } else {
      currentTile.setPlaneTransit(null);
    }

    changeAssetDetail();
  };

  const setDefaultGroundHeight = value => {
    currentTile.setDefaultGroundHeight(value);
    changeAssetDetail();
  };

  const deleteAsset = () => {
    sharedStore.deleteTileAsset(id);
    editorClient.deleteTileAsset(id);
  };

  const changeAssetDetail = () => {
    editorClient.updateTileAsset(currentTile, null, null, null, null);
  };

  const changeAssetImage = async (fileInput, tileImageUsage) => {
    try {
      const file = fileInput.files[0];
      if (!file) return;
      await tileImageStore.provideTileAssetFromLocalFilesystem(currentTile.id, tileImageUsage, file);
      fileInput.value = null;
    } catch (e) {
      errorStore.addErrorFromErrorObject(e);
    }
  };

  const deleteAssetImage = async tileImageUsage => {
    await tileImageStore.provideTileAssetFromLocalFilesystem(currentTile.id, tileImageUsage, null);
  };

  return /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_id")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    disabled: true,
    type: "text",
    value: displayId
  }), "\xA0", /*#__PURE__*/React.createElement(DeleteButton, {
    onClick: deleteAsset
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faMinusCircle
  }), " ", t("editor.tile_asset_delete")), "\xA0", /*#__PURE__*/React.createElement("button", {
    onClick: () => undoableSelectTileAsset(tileAssetEditorStore, null)
  }, t("editor.deselect")))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.translated_name")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: currentTile.localizedName.get(gameStore.languageKey, false),
    placeholder: currentTile.localizedName.get(gameStore.languageKey, true),
    onChange: ({
      target
    }) => setLocalizedName(target.value)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_tags")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: currentTile.tags.join(" "),
    onChange: ({
      target
    }) => setTags(target.value.split(" "))
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_visibility_in_editor")), /*#__PURE__*/React.createElement("td", null, tileVisibilities.map(visibility => /*#__PURE__*/React.createElement(VisibilityInputWrapper, {
    key: visibility,
    className: TileVisibility[visibility]
  }, /*#__PURE__*/React.createElement("input", {
    type: "radio",
    id: "visibility" + visibility,
    value: visibility,
    checked: currentTile.visibilityInEditor === visibility,
    onChange: () => setVisibilityInEditor(visibility)
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: "visibility" + visibility
  }, t("editor.tile_asset_visibility_" + TileVisibility[visibility])))))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_layer_type")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("select", {
    value: currentTile.layerType,
    onChange: ({
      target
    }) => setLayer(parseInt(target.value))
  }, /*#__PURE__*/React.createElement("option", {
    key: 0,
    value: 0
  }, t("editor.tile_asset_layer_type_ground")), /*#__PURE__*/React.createElement("option", {
    key: 1,
    value: 1
  }, t("editor.tile_asset_layer_type_decoration"))))), allTileImageUsages.filter(tileImageUsage => availableImageUsagesForLayerType.get(currentTile.layerType).indexOf(tileImageUsage) >= 0).map(tileImageUsage => {
    const key = TileImageUsage[tileImageUsage];
    const imageProperties = currentTile.imageProperties(tileImageUsage);
    const canBeAdjusted = imageProperties && tileImageUsage !== TileImageUsage.WaterMask && tileImageUsage !== TileImageUsage.WaterMaskForeground;
    const hasFailed = imageProperties && tileImageStore.hasFailed(id, tileImageUsage);
    return /*#__PURE__*/React.createElement("tr", {
      key: key
    }, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_image_usage_" + TileLayerType[currentTile.layerType] + "_" + key)), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Button, {
      disabled: !imageProperties || anyAssetStillLoading,
      onClick: () => download(currentTile.id, tileImageUsage)
    }, imageProperties ? /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faDownload
    }) : /*#__PURE__*/React.createElement(Fragment, null, "\xA0")), "\xA0", /*#__PURE__*/React.createElement(DeleteButton, {
      disabled: !imageProperties || anyAssetStillLoading,
      onClick: () => deleteAssetImage(tileImageUsage)
    }, imageProperties ? /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faMinusCircle
    }) : /*#__PURE__*/React.createElement(Fragment, null, "\xA0")), "\xA0", /*#__PURE__*/React.createElement(FileUploadButton, {
      disabled: anyAssetStillLoading,
      key: currentTile.$modelId,
      type: "file",
      accept: "image/png",
      onChange: ({
        target
      }) => changeAssetImage(target, tileImageUsage)
    }), "\xA0", canBeAdjusted && /*#__PURE__*/React.createElement(React.Fragment, null, "x ", /*#__PURE__*/React.createElement(CoordinateInput, {
      disabled: anyAssetStillLoading,
      positionProps: imageProperties.positionOnTile,
      yCoordinate: false,
      changeAssetDetail: changeAssetDetail
    }), "\xA0 y ", /*#__PURE__*/React.createElement(CoordinateInput, {
      disabled: anyAssetStillLoading,
      positionProps: imageProperties.positionOnTile,
      yCoordinate: true,
      changeAssetDetail: changeAssetDetail
    }), "\xA0 frames ", /*#__PURE__*/React.createElement(IntInputField, {
      disabled: anyAssetStillLoading,
      value: imageProperties === null || imageProperties === void 0 ? void 0 : imageProperties.frames,
      onlyPositive: true,
      onChange: value => setFrames(imageProperties, value)
    }), "\xA0 sec ", /*#__PURE__*/React.createElement(FloatInputField, {
      disabled: anyAssetStillLoading,
      value: imageProperties === null || imageProperties === void 0 ? void 0 : imageProperties.animationDuration,
      onChange: value => setAnimationDuration(imageProperties, value)
    })), hasFailed && /*#__PURE__*/React.createElement(ErrorDiv, null, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faExclamationCircle
    }), " ", t("editor.error_image_lost_please_reupload"))));
  }), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_blocked_directions")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("label", null, /*#__PURE__*/React.createElement("input", {
    type: "checkbox",
    checked: tileAssetEditorStore.editBlockedDirections,
    onChange: () => tileAssetEditorStore.toggleEditBlockedDirections()
  }), " ", t("editor.edit_blocked_directions")), " \xA0\xA0")), currentTile.layerType === TileLayerType.Ground && /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_plane_transit")), /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_plane_transit_in_direction"), "\xA0", /*#__PURE__*/React.createElement("select", {
    value: currentTile.planeTransit ? currentTile.planeTransit.direction : -1,
    onChange: ({
      target
    }) => setPlaneTransit(parseInt(target.value))
  }, /*#__PURE__*/React.createElement("option", {
    key: -1,
    value: -1
  }), /*#__PURE__*/React.createElement("option", {
    key: 0,
    value: Direction.North
  }, t("editor.tile_asset_north")), /*#__PURE__*/React.createElement("option", {
    key: 1,
    value: Direction.West
  }, t("editor.tile_asset_west"))), "\xA0", t("editor.tile_asset_plane_transit_target"), ": \xA0", t("editor.tile_asset_plane_transit_target_offset_x"), " ", /*#__PURE__*/React.createElement(IntInputField, {
    disabled: currentTile.planeTransit == null,
    value: (_currentTile$planeTra = currentTile.planeTransit) === null || _currentTile$planeTra === void 0 ? void 0 : _currentTile$planeTra.targetXOffset,
    onChange: value => setTargetXOffset(value)
  }), "\xA0", t("editor.tile_asset_plane_transit_target_offset_y"), " ", /*#__PURE__*/React.createElement(IntInputField, {
    disabled: currentTile.planeTransit == null,
    value: (_currentTile$planeTra2 = currentTile.planeTransit) === null || _currentTile$planeTra2 === void 0 ? void 0 : _currentTile$planeTra2.targetYOffset,
    onChange: value => setTargetYOffset(value)
  }), "\xA0", t("editor.tile_asset_plane_transit_target_offset_height"), " ", /*#__PURE__*/React.createElement(IntInputField, {
    disabled: currentTile.planeTransit == null,
    value: (_currentTile$planeTra3 = currentTile.planeTransit) === null || _currentTile$planeTra3 === void 0 ? void 0 : _currentTile$planeTra3.heightDifference,
    onChange: value => setTransitHeightDifference(value)
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_plane")), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("select", {
    disabled: !!currentTile.planeTransit,
    value: "" + (currentTile.isHeightFlexible ? null : currentTile.defaultGroundHeight),
    onChange: e => setDefaultGroundHeight(e.target.value === "" ? null : parseInt(e.target.value))
  }, /*#__PURE__*/React.createElement("option", {
    value: ""
  }, t("editor.tile_asset_default_ground_height_is_flexible")), gameConstants.planes.map(idx => /*#__PURE__*/React.createElement("option", {
    key: idx,
    value: idx
  }, idx + 1)))))), currentTile.layerType === TileLayerType.Decoration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_offset")), /*#__PURE__*/React.createElement("td", null, "x ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.offsetX,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setOffsetX(value);
      changeAssetDetail();
    }
  }), "\xA0 y ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.offsetY,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setOffsetY(value);
      changeAssetDetail();
    }
  }), "\xA0 z ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.internalOffsetZ,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setInternalOffsetZ(value);
      changeAssetDetail();
    }
  }))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_size")), /*#__PURE__*/React.createElement("td", null, "x ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.size.x,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setSizeX(value);
      changeAssetDetail();
    }
  }), "\xA0 y ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.size.y,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setSizeY(value);
      changeAssetDetail();
    }
  }), "\xA0 z ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.size.z,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setSizeZ(value);
      changeAssetDetail();
    }
  })))), currentTile.layerType === TileLayerType.Ground && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_size")), /*#__PURE__*/React.createElement("td", null, "x ", /*#__PURE__*/React.createElement(IntInputField, {
    value: currentTile.size.x,
    onlyPositive: true,
    onChange: value => {
      currentTile.setSizeX(value);
      changeAssetDetail();
    }
  }), "\xA0 y ", /*#__PURE__*/React.createElement(IntInputField, {
    value: currentTile.size.y,
    onlyPositive: true,
    onChange: value => {
      currentTile.setSizeY(value);
      changeAssetDetail();
    }
  }), "\xA0 z ", /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.size.z,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setSizeZ(value);
      changeAssetDetail();
    }
  }))), !currentTile.size.isFlat && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, t("editor.tile_asset_conflict_resolution_origin")), /*#__PURE__*/React.createElement("td", {
    onMouseEnter: () => tileAssetEditorStore.setHoveringConflictResolutionOrigin(true),
    onMouseLeave: () => tileAssetEditorStore.setHoveringConflictResolutionOrigin(false)
  }, /*#__PURE__*/React.createElement(FloatInputField, {
    value: currentTile.conflictResolutionOrigin,
    onlyPositive: true,
    step: tileOffsetAndSizeStep,
    onChange: value => {
      currentTile.setConflictResolutionOrigin(clamp(value, 0, 1));
      changeAssetDetail();
    }
  }), "\xA0", t("editor.tile_asset_conflict_resolution_origin_back"), "\xA0", /*#__PURE__*/React.createElement("input", {
    type: "range",
    min: "0",
    max: "1",
    step: conflictResolutionOriginStep,
    value: currentTile.conflictResolutionOrigin,
    onChange: e => {
      currentTile.setConflictResolutionOrigin(+e.target.value);
      changeAssetDetail();
    }
  }), "\xA0", t("editor.tile_asset_conflict_resolution_origin_front"))), anyAssetStillLoading && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faCog,
    spin: true
  }), "\xA0", t("editor.loading_please_wait")))));
});
export const TileAssetConfigurator = observer(() => {
  const {
    t
  } = useTranslation();
  const [newAssetId, setNewAssetId] = useState("");
  const {
    selectedTileAssetId
  } = tileAssetEditorStore.placementSelection;

  const createNewTileAsset = () => {
    const layerType = tileAssetEditorStore.currentLayerType;
    const newTileAsset = new TileAssetModel({
      id: newAssetId,
      layerType
    });
    sharedStore.addTileAsset(newTileAsset);
    undoableSelectTileAsset(tileAssetEditorStore, newAssetId);
    editorClient.updateTileAsset(newTileAsset, null, null, null, null);
    setNewAssetId("");
  };

  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: newAssetId,
    onChange: ({
      target
    }) => setNewAssetId(tileImageStore.uniqueAssetId("", target.value))
  }), "\xA0", /*#__PURE__*/React.createElement("button", {
    disabled: newAssetId === "",
    onClick: createNewTileAsset
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPlus
  }), " ", t("editor.tile_asset_create")))))), selectedTileAssetId == null || /*#__PURE__*/React.createElement(TileConfiguration, {
    id: selectedTileAssetId
  }));
});